import type { ChangeEvent, CSSProperties } from 'react';
import { sx } from '../../ui/designStyles';
import { EmptyState } from '../../ui/states';
import { usePeerStaffing, peerOf } from './staffing';
import { A, DASH, type IntelState, type IntelTab, type TabProps } from './common';
import type { Deal } from './model';
import { ModeBar, seg, segS } from './simulate/ModeBar';
import { useSavedScenarios } from './simulate/saved';
import {
  STRUCTS,
  STRUCT_NOTE,
  STRUCT_SHORT,
  dealKey,
  defaultStruct,
  runDealModel,
  type InKey,
  type Inputs,
  type Loan,
  type Sector,
  type Structure,
} from './simulate/dealModel';
import './simulate/simulate.css';
import { BuildLabel } from '../../ui/buildLabels';

/** Deal model state keys (V4 dsSector, dsLoan, dsIn, dsStruct, dsLift). */
type DsState = IntelState & { dsSector?: Sector; dsLoan?: Loan; dsIn?: Inputs; dsStruct?: Structure; dsLift?: boolean };

const MINUS = '−';
/** $M with the design's minus sign. */
const Mm = (x: number) => {
  const a = Math.abs(x);
  const t = a >= 1 ? '$' + a.toFixed(a < 10 ? 2 : 1) + 'M' : '$' + Math.round(a * 1000) + 'K';
  return (x < 0 ? MINUS : '') + t;
};
const P = (x: number, d?: number) => (x < 0 ? MINUS : '') + Math.abs(x * 100).toFixed(d === undefined ? 1 : d) + '%';

const PANEL = 'box-sizing:border-box;min-width:0;min-height:0;display:flex;flex-direction:column;border:1px solid rgba(34,39,46,.13);border-radius:11px;background:#FCFAF4;padding:11px 13px;overflow:hidden;';
const CAPS = 'font-size:14px;letter-spacing:.11em;text-transform:uppercase;color:#54606C;font-weight:600;';
const BADGE = 'flex:0 0 auto;white-space:nowrap;font-size:14px;font-weight:700;letter-spacing:.08em;color:#54606C;background:rgba(34,39,46,.1);border-radius:3px;padding:1px 5px;';
const INPUT = 'box-sizing:border-box;width:74px;height:26px;padding:0 6px;border:1px solid rgba(34,39,46,.2);border-radius:6px;background:#fff;font:inherit;font-size:16px;font-variant-numeric:tabular-nums;text-align:right;color:#22272E;';
const PLAIN = 'font-size:16px;font-weight:800;font-variant-numeric:tabular-nums;white-space:nowrap;';

/**
 * Simulate > Deal model (V4 dsIs, T15745; aiDealVals L31144): values the purchase of the
 * facility on the right, as the business, the building, or both. Client-side math over the
 * facility's deal card (model.ts), MODELED throughout; saved scenarios persist per user and
 * facility (GET/POST /api/aithora/intelligence/scenarios).
 */
export function DealModelTab(p: TabProps) {
  return (
    <>
      <ModeBar p={p} tab="deal" />
      {p.f ? <DealModel p={p} f={p.f} /> : <EmptyState title="Nothing to model yet" body="Track a facility from the catalog and model it here." />}
    </>
  );
}

function DealModel({ p, f }: { p: TabProps; f: Deal }) {
  const st = p.st as DsState;
  const set = p.set as (patch: DsState) => void;
  const sector: Sector = st.dsSector === 'al' || st.dsSector === 'mc' || st.dsSector === 'snf' ? st.dsSector : 'snf';
  const loan: Loan = st.dsLoan === 'hud' ? 'hud' : 'bank';
  const struct: Structure = st.dsStruct && STRUCTS.includes(st.dsStruct) ? st.dsStruct : defaultStruct(sector);
  const lift = !!st.dsLift;
  const m = runDealModel({ f, sector, loan, struct, lift, edits: st.dsIn ?? {} });
  const { SEC, D, val, isSnf, cur, now, R, band, leased } = m;
  const key = dealKey(f, sector);
  const peers = usePeerStaffing(p.client, [{ type: f.type, state: f.stt }]);
  const saved = useSavedScenarios(p.client, f, p.readOnly);

  const setIn = (k: InKey) => (e: ChangeEvent<HTMLInputElement>) => {
    const n = parseFloat(e.target.value);
    set({ dsIn: { ...D, __f: key, [k]: Number.isNaN(n) ? undefined : n } });
  };
  const inp = (k: InKey, label: string, unit: string, step: number) => ({ k, label, unit, step: String(step), value: String(Math.round((k === 'rent' ? cur.rent : val(k)) * 100) / 100) });
  // The payer mix carries the server's label (payer-mix.ts): COST REPORT FY<year>, MODELED or ESTIMATED.
  const mixTag = ' · ' + f.payerMixLabel;
  const privLabel = f.payerMixLabel.startsWith('COST REPORT') ? 'Other mix (private, insurance, MA)' : 'Private mix';
  const opIn = (isSnf ? [inp('occ', 'Occupancy', '%', 0.5), inp('mcare', 'Medicare mix' + mixTag, '%', 1), inp('priv', privLabel + mixTag, '%', 1)] : [inp('occ', 'Occupancy', '%', 0.5)]).concat([
    inp('agency', 'Agency labor', '% of labor', 0.5),
    inp('margin', 'EBITDAR margin', '%', 0.5),
    inp('fee', 'Management fee', '% of revenue', 0.5),
    inp('mult', 'Business multiple', '× EBITDA', 0.1),
    inp('grow', 'EBITDAR growth', '% / yr', 0.25),
    inp('hold', 'Hold', 'years', 1),
  ]);
  const rbIn = isSnf ? [inp('mcareG', 'Medicare rate growth', '% / yr', 0.1), inp('mcaidG', 'Medicaid rate growth', '% / yr', 0.1), inp('earnBack', 'Quality withhold earned back', '%', 5)] : [];
  const finOnly = struct === 'opco';
  const reIn = (struct === 'managed' ? [] : [inp('rent', 'Annual rent', '$M', 0.05), inp('covT', 'Target rent coverage', '×', 0.05), inp('esc', 'Rent escalator', '% / yr', 0.25)])
    .concat(finOnly ? [] : [inp('cap', 'Entry cap rate', '%', 0.25), inp('exitCap', 'Exit cap rate', '%', 0.25)])
    .concat([inp('ltv', 'Loan to value', '%', 5), inp('rate', 'Loan rate', '%', 0.25), inp('amort', 'Amortization', 'years', 1), inp('dscrMin', 'Lender minimum DSCR', '×', 0.05)]);

  const cov = Math.round(now.cov * 1000) / 1000;
  const covT = val('covT');
  const under = cov < covT - 0.005;
  const covTone = cov < 1.2 ? '#B3261E' : under ? '#8A5A00' : '#1F6B52';
  const covWord = cov < 1.2 ? 'Below 1.2×: landlords re-price or walk' : under ? 'Under the ' + covT.toFixed(2) + '× target' : 'At or above the ' + covT.toFixed(2) + '× target';
  const dscrMin = val('dscrMin');
  const dscr = R.dscr;
  const dscrTone = dscr === null ? '#54606C' : dscr < dscrMin ? '#B3261E' : dscr < dscrMin + 0.15 ? '#8A5A00' : '#1F6B52';

  type Row = [string, string, string, (string | null)?];
  const rowsOp: Row[] = [['Revenue', Mm(now.rev), isSnf ? Math.round((now.gross * 1e6) / (f.beds * now.occ * 365)) + ' a resident-day before the withhold' : '$' + SEC.priv + ' a resident-day, private pay']];
  if (isSnf) rowsOp.push(['Medicare quality withhold', MINUS + Mm(now.withhold - now.earned), '2% withheld, ' + Math.round(Math.min(1, val('earnBack') / 100 + (lift ? 0.2 : 0)) * 100) + '% earned back']);
  rowsOp.push(['EBITDARM', Mm(now.ebitdarm), 'before rent and management fee'], ['Management fee', MINUS + Mm(now.mgmt), val('fee') + '% of revenue'], ['EBITDAR', Mm(now.ebitdar), 'before rent']);
  if (struct === 'managed') rowsOp.push(['Value', Mm(now.noiVal), 'NOI ÷ ' + val('cap') + '% cap rate']);
  else
    rowsOp.push(
      ['Rent paid', MINUS + Mm(now.rent), 'to the building owner'],
      ['EBITDA', Mm(now.opEbitda), 'what the business keeps', now.opEbitda < 0 ? '#B3261E' : null],
      ['Business value', Mm(now.opVal), 'EBITDA × ' + val('mult').toFixed(1)],
    );
  const rowsRe: Row[] = [];
  if (!finOnly) {
    if (struct === 'managed')
      rowsRe.push(['Property value', Mm(now.noiVal), 'NOI ÷ ' + val('cap') + '% cap rate'], ['Per bed', '$' + Math.round((now.noiVal * 1e6) / f.beds / 1000) + 'K', f.beds + ' beds']);
    else
      rowsRe.push(
        ['Rent received', Mm(now.rent), 'escalates ' + val('esc') + '% a year'],
        ['Building value', Mm(now.reVal), 'rent ÷ ' + val('cap') + '% cap rate'],
        ['Per bed', '$' + Math.round((now.reVal * 1e6) / f.beds / 1000) + 'K', f.beds + ' beds'],
      );
  }
  rowsRe.push(
    ['Loan', Mm(R.debt), (loan === 'hud' ? 'HUD 232' : 'Bank') + ', ' + val('amort') + '-yr amortization'],
    ['Debt service', Mm(R.ds) + ' / yr', val('rate') + '% rate'],
    ['DSCR', dscr === null ? DASH : dscr.toFixed(2) + '×', 'lender minimum ' + dscrMin.toFixed(2) + '×', dscrTone],
  );

  // Risks. Staffing reads against same-type, same-state peers: there is no federal staffing floor.
  const hprd = f.hprd || 3.4;
  const stars = f.stars || 3;
  const opex = now.rev - now.ebitdarm;
  const peer = peerOf(peers, f.type, f.stt);
  const med = peer?.median ?? null;
  const staffWhy = hprd.toFixed(2) + ' nurse hrs per resident day' + (med != null ? ' vs ' + med.toFixed(2) + ' typical for peers' : '');
  const staffTone = med == null ? 'amb' : hprd < med * 0.92 ? 'red' : hprd < med ? 'amb' : 'grn';
  const TONE: Record<string, [string, string, string]> = {
    red: ['#B3261E', 'rgba(179,38,30,.12)', 'HIGH'],
    amb: ['#8A5A00', 'rgba(184,134,11,.16)', 'WATCH'],
    grn: ['#1F6B52', 'rgba(47,138,111,.13)', 'OK'],
  };
  const RISK: [string, string, string, IntelTab | 'deal'][] = [
    ['Change of ownership', (isSnf ? 'CMS provider agreement and CHOW filing; ' : 'State license; ') + 'plan 60–90 days', 'amb', 'track'],
    ['Cash needed at close', Mm((opex * 45) / 365) + '–' + Mm((opex * 60) / 365) + ': 45–60 days of operating cost, often required by the state', 'amb', 'deal'],
    ['Incidents after diligence', 'Ask for incident reporting to close, with a right to re-price or walk', 'amb', 'track'],
    ['Payer contracts', 'Managed-care and Medicaid contracts may not transfer with the sale', isSnf ? 'amb' : 'grn', 'track'],
    ['Survey history', stars + '★ CMS rating', stars <= 2 ? 'red' : stars === 3 ? 'amb' : 'grn', 'bench'],
    ['Staffing', staffWhy, staffTone, 'bench'],
  ];
  if (isSnf) RISK.push(['Medicaid rate risk', P(now.md, 0) + ' of days paid by Medicaid', now.md > 0.6 ? 'red' : now.md > 0.45 ? 'amb' : 'grn', 'bench']);
  if (leased) RISK.push(['Rent coverage', cov.toFixed(2) + '× EBITDAR to rent', cov < 1.2 ? 'red' : under ? 'amb' : 'grn', 'deal']);
  RISK.push(['Debt service coverage', dscr === null ? 'no loan' : dscr.toFixed(2) + '× vs ' + dscrMin.toFixed(2) + '× minimum', dscr === null ? 'grn' : dscr < dscrMin ? 'red' : dscr < dscrMin + 0.15 ? 'amb' : 'grn', 'deal']);

  const irrTxt = R.irr === null ? DASH : P(R.irr);
  const bandOk = band[0] !== null && band[1] !== null;
  const bandLo = bandOk ? Math.min(band[0] as number, band[1] as number) : 0;
  const bandHi = bandOk ? Math.max(band[0] as number, band[1] as number) : 0;
  const split =
    struct === 'both' || struct === 'slb' ? 'Business ' + Mm(now.opVal) + ' · building ' + Mm(now.reVal) : struct === 'opco' ? 'Business ' + Mm(now.opVal) : struct === 'managed' ? 'Building and business ' + Mm(now.noiVal) : 'Building ' + Mm(now.reVal);
  const showOp = struct !== 'propco';
  const reTitle = finOnly ? 'Lease and financing' : struct === 'managed' ? 'The building and loan' : 'The building';
  const reSub = finOnly ? 'You lease the building. Rent shows on the left as a cost; set it here.' : struct === 'managed' ? 'No lease. The whole property is valued on its NOI and cap rate.' : 'Land and building. Valued on rent and cap rate.';
  const anyChanged = Object.keys(D).some((k) => k !== '__f' && D[k as InKey] !== undefined);

  const memo = () => {
    const lines = [
      'IC MEMO (MODELED) · ' + f.name,
      'Sector: ' + SEC.name,
      'Structure: ' + STRUCT_NOTE[struct],
      'Aithora lift: ' + (lift ? 'included (MODELED)' : 'not included'),
      '',
      'Price: ' + Mm(R.entry) + ' · equity in ' + Mm(R.eq),
      'IRR: ' + irrTxt + ' (' + (bandOk ? 'P10–P90 ' + P(bandLo) + '–' + P(bandHi) : 'range n/a') + ')',
      'MOIC: ' + R.moic.toFixed(2) + 'x',
      leased ? 'Rent coverage: ' + cov.toFixed(2) + 'x EBITDAR, ' + now.covM.toFixed(2) + 'x EBITDARM (target ' + covT.toFixed(2) + 'x)' : 'No lease (owner keeps profit, pays manager)',
      'DSCR: ' + (dscr === null ? 'no loan' : dscr.toFixed(2) + 'x vs ' + dscrMin.toFixed(2) + 'x minimum (' + (loan === 'hud' ? 'HUD 232' : 'bank') + ')'),
      '',
      'Business: revenue ' + Mm(now.rev) + ', EBITDARM ' + Mm(now.ebitdarm) + ', EBITDAR ' + Mm(now.ebitdar) + (leased ? ', EBITDA ' + Mm(now.opEbitda) : ''),
      leased ? 'Building: rent ' + Mm(now.rent) + ', cap ' + val('cap') + '%, value ' + Mm(now.reVal) : 'Value: ' + Mm(now.noiVal) + ' at ' + val('cap') + '% cap',
      '',
      'Risks:',
      ...RISK.map((r) => '- ' + r[0] + ' [' + TONE[r[2]][2] + ']: ' + r[1]),
      '',
      'Every figure is a modeled estimate. Assumptions are editable in the console.',
    ];
    const b = new Blob([lines.join('\n')], { type: 'text/plain' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(b);
    a.download = 'IC memo - ' + f.name + '.txt';
    a.click();
  };
  const save = () =>
    saved.save({
      name: (lift ? 'Aithora · ' : '') + STRUCT_SHORT[struct],
      irr: irrTxt,
      moic: R.moic.toFixed(2) + '×',
      cov: leased ? cov.toFixed(2) + '×' : 'n/a',
      eq: Mm(R.eq),
      inputs: { sector, loan, struct, lift, edits: D },
    });

  return (
    <div data-screen-label="Simulate" style={sx('display:flex;flex-direction:column;gap:10px;min-width:0;height:calc(100vh - 262px);min-height:460px;')}>
      <div style={sx('flex:0 0 auto;display:flex;align-items:center;gap:8px;flex-wrap:wrap;row-gap:6px;')}>
        <div style={sx('min-width:0;margin-right:6px;')}>
          <div style={sx('font-size:19px;font-weight:700;letter-spacing:-.018em;')}>
            Acquisition model &middot; <span>{f.name}</span>
          </div>
          <div style={sx('font-size:16px;color:#54606C;')}>{STRUCT_NOTE[struct] + ' · ' + SEC.name}</div>
        </div>
        <div style={sx('display:flex;gap:4px;')}>
          {(
            [
              ['snf', 'Nursing home'],
              ['al', 'Assisted living'],
              ['mc', 'Memory care'],
            ] as [Sector, string][]
          ).map(([k, label]) => (
            <A key={k} onClick={() => set({ dsSector: k, dsStruct: k === 'snf' ? 'opco' : 'managed' })} style={segS(sector === k)}>
              {label}
            </A>
          ))}
        </div>
        <span style={sx('flex:1;')} />
        {STRUCTS.map((x) => (
          <A key={x} title={STRUCT_NOTE[x]} onClick={() => set({ dsStruct: x })} style={seg(struct === x)}>
            {STRUCT_SHORT[x]}
          </A>
        ))}
        <A onClick={() => set({ dsLift: !lift })} style={seg(lift)}>
          {lift ? 'With Aithora ✓' : 'Add Aithora lift'}
        </A>
      </div>

      <div style={sx('flex:1 1 auto;min-height:0;display:grid;grid-template-columns:minmax(0,1fr) minmax(220px,.8fr) minmax(0,1fr);gap:10px;')}>
        <div style={sx(PANEL)}>
          {showOp ? (
            <>
              <div style={sx('display:flex;align-items:center;gap:7px;')}>
                <span style={sx(CAPS)}>The business</span>
                <BuildLabel><span style={sx(BADGE)}>MODELED</span></BuildLabel>
              </div>
              <div style={sx('font-size:14px;color:#54606C;margin:2px 0 6px;')}>Licenses, staff, residents, payers. Valued on earnings.</div>
              <div style={sx('flex:1 1 auto;min-height:0;overflow-y:auto;')}>
                {rowsOp.map((r) => (
                  <FigRow key={r[0]} r={r} />
                ))}
                <div style={sx(CAPS + 'margin:10px 0 2px;')}>Assumptions</div>
                {opIn.map((i) => (
                  <InRow key={i.k} i={i} onChange={setIn(i.k)} />
                ))}
                {isSnf && (
                  <>
                    <div style={sx(CAPS + 'margin:10px 0 2px;')}>Reimbursement</div>
                    <div style={sx('font-size:14px;color:#54606C;margin-bottom:2px;')}>FY2026 Medicare update 3.2%. CMS withholds 2% of Medicare Part A for the quality program and pays part back.</div>
                    {rbIn.map((i) => (
                      <InRow key={i.k} i={i} onChange={setIn(i.k)} />
                    ))}
                  </>
                )}
              </div>
            </>
          ) : (
            <div style={sx('margin:auto 0;font-size:16px;color:#54606C;line-height:1.5;text-align:center;padding:0 10px;text-wrap:pretty;')}>
              Not part of this deal. You own the building and lease it to an operator, so only the rent matters to you. Switch to Buy both to model the business too.
            </div>
          )}
        </div>

        <div style={sx('display:flex;flex-direction:column;gap:10px;min-width:0;min-height:0;')}>
          <div style={sx(PANEL + 'flex:0 0 auto;')}>
            <div style={sx('display:flex;align-items:center;gap:7px;')}>
              <span style={sx(CAPS)}>Deal return</span>
              <BuildLabel><span style={sx(BADGE)}>MODELED</span></BuildLabel>
            </div>
            <div style={sx('display:flex;align-items:baseline;gap:8px;margin-top:4px;')}>
              <span style={sx('font-size:26px;font-weight:800;letter-spacing:-.02em;font-variant-numeric:tabular-nums;color:' + (R.irr === null ? '#54606C' : R.irr >= 0.15 ? '#1F6B52' : R.irr >= 0.1 ? '#22272E' : '#B3261E') + ';')}>{irrTxt}</span>
              <span style={sx('font-size:14px;color:#54606C;')}>
                IRR &middot; <span>{Math.round(val('hold')) + '-yr hold'}</span>
              </span>
            </div>
            <div style={sx('font-size:14px;color:#3C454F;')}>{bandOk ? 'P10–P90 ' + P(bandLo) + ' to ' + P(bandHi) : 'range not computable'}</div>
            <div style={sx('display:grid;grid-template-columns:repeat(3,minmax(0,1fr));gap:6px;margin-top:8px;')}>
              <Fig k="Equity in" v={Mm(R.eq)} />
              <Fig k="MOIC" v={R.moic.toFixed(2) + '×'} />
              <Fig k="Price" v={Mm(R.entry)} />
            </div>
            <div style={sx('font-size:14px;color:#54606C;margin-top:5px;')}>{split}</div>
          </div>
          <div style={sx(PANEL + 'flex:0 0 auto;')}>
            {leased ? (
              <>
                <div style={sx(CAPS)}>Rent coverage</div>
                <div style={sx('display:flex;align-items:baseline;gap:8px;flex-wrap:wrap;')}>
                  <span style={sx('font-size:28px;font-weight:800;letter-spacing:-.02em;font-variant-numeric:tabular-nums;color:' + covTone + ';')}>{cov.toFixed(2) + '×'}</span>
                  <span style={sx('font-size:14px;color:#54606C;white-space:nowrap;')}>
                    EBITDAR &middot; <span>{now.covM.toFixed(2) + '×'}</span> EBITDARM
                  </span>
                </div>
                <div style={sx('font-size:14px;font-weight:700;color:' + covTone + ';')}>{covWord}</div>
                <div style={sx('position:relative;height:8px;border-radius:4px;margin:8px 0 3px;background:linear-gradient(90deg,rgba(179,38,30,.55) 0%,rgba(179,38,30,.55) 28.6%,rgba(184,134,11,.55) 28.6%,rgba(184,134,11,.55) 42.9%,rgba(47,138,111,.55) 42.9%);')}>
                  <div style={sx('position:absolute;top:-2px;width:0;height:12px;border-left:2px dashed #2F5C97;left:' + Math.max(0, Math.min(100, ((covT - 0.8) / 1.4) * 100)).toFixed(1) + '%;')} />
                  <div style={sx('position:absolute;top:-4px;width:4px;height:16px;border-radius:2px;margin-left:-2px;background:#22272E;left:' + Math.max(0, Math.min(100, ((cov - 0.8) / 1.4) * 100)).toFixed(1) + '%;')} />
                </div>
                <div style={sx('display:flex;justify-content:space-between;font-size:14px;color:#54606C;font-variant-numeric:tabular-nums;')}>
                  <span>0.8&times;</span>
                  <span>1.2&times; floor</span>
                  <span>
                    target <span>{covT.toFixed(2) + '×'}</span>
                  </span>
                  <span>2.2&times;</span>
                </div>
                <div style={sx('font-size:14px;color:#54606C;margin-top:4px;')}>The business pays the building. Landlords target about 1.3&times; on a new nursing-home lease and re-price or walk below 1.2&times;.</div>
              </>
            ) : (
              <>
                <div style={sx(CAPS)}>No lease</div>
                <div style={sx('font-size:16px;color:#3C454F;line-height:1.45;margin-top:4px;text-wrap:pretty;')}>You own the building and the business and pay a manager. Profit and losses are yours, so occupancy and wage swings hit returns directly.</div>
              </>
            )}
          </div>
          <div style={sx(PANEL + 'flex:1 1 auto;')}>
            <div style={sx(CAPS)}>Deal risks</div>
            <div style={sx('flex:1 1 auto;min-height:0;overflow-y:auto;margin-top:4px;')}>
              {RISK.map((r) => {
                const t = TONE[r[2]];
                return (
                  <A key={r[0]} hover="intel-h-risk" onClick={() => r[3] !== 'deal' && p.go(r[3] as IntelTab)} style="display:flex;align-items:center;gap:8px;padding:5px 0;border-bottom:1px solid rgba(34,39,46,.07);text-decoration:none;color:#22272E;">
                    <span style={sx('flex:1 1 auto;min-width:0;')}>
                      <span style={sx('display:block;font-size:16px;font-weight:600;')}>{r[0]}</span>
                      <span style={sx('display:block;font-size:14px;color:#54606C;overflow-wrap:break-word;')}>{r[1]}</span>
                    </span>
                    <span style={sx('flex:0 0 auto;font-size:14px;font-weight:800;letter-spacing:.08em;border-radius:4px;padding:2px 6px;color:' + t[0] + ';background:' + t[1] + ';')}>{t[2]}</span>
                  </A>
                );
              })}
            </div>
          </div>
        </div>

        <div style={sx('display:flex;flex-direction:column;gap:10px;min-width:0;min-height:0;')}>
          <div style={sx(PANEL + 'flex:1 1 auto;')}>
            <div style={sx('display:flex;align-items:center;gap:7px;')}>
              <span style={sx(CAPS)}>{reTitle}</span>
              <BuildLabel><span style={sx(BADGE)}>MODELED</span></BuildLabel>
            </div>
            <div style={sx('font-size:14px;color:#54606C;margin:2px 0 6px;')}>{reSub}</div>
            <div style={sx('flex:1 1 auto;min-height:0;overflow-y:auto;')}>
              {rowsRe.map((r) => (
                <FigRow key={r[0]} r={r} />
              ))}
              <div style={sx('display:flex;align-items:center;gap:6px;margin:10px 0 2px;')}>
                <span style={sx(CAPS)}>Assumptions</span>
                <span style={sx('flex:1;')} />
                {(
                  [
                    ['bank', 'Bank loan'],
                    ['hud', 'HUD 232'],
                  ] as [Loan, string][]
                ).map(([k, label]) => (
                  <A key={k} onClick={() => set({ dsLoan: k, dsIn: { ...D, __f: key, ltv: undefined, rate: undefined, amort: undefined, dscrMin: undefined } })} style={segS(loan === k)}>
                    {label}
                  </A>
                ))}
              </div>
              {reIn.map((i) => (
                <InRow key={i.k} i={i} onChange={setIn(i.k)} />
              ))}
            </div>
          </div>
          <div style={sx(PANEL + 'flex:0 0 auto;')}>
            <div style={sx(CAPS)}>Backtest &middot; if they had had Aithora</div>
            <div style={sx('font-size:16px;color:#3C454F;line-height:1.45;margin-top:4px;text-wrap:pretty;')}>
              Not available yet. It needs 3&ndash;6 months of this facility&apos;s claims and cost reports loaded. Until then the forward model above is the only estimate, and it is marked MODELED.
            </div>
            <A onClick={() => p.go('simulate', { ptMode: 'Backtest' })} style="margin-top:8px;align-self:flex-start;display:inline-flex;align-items:center;height:30px;padding:0 12px;border-radius:7px;font-size:16px;font-weight:700;color:#fff;background:#2F5C97;text-decoration:none;">
              See the realized backtest &rarr;
            </A>
          </div>
        </div>
      </div>

      <div style={sx('flex:0 0 auto;display:flex;align-items:center;gap:8px;flex-wrap:wrap;row-gap:6px;')}>
        {!p.readOnly && (
          <A onClick={save} style="display:inline-flex;align-items:center;height:32px;padding:0 14px;border-radius:8px;font-size:16px;font-weight:700;text-decoration:none;background:#2F5C97;color:#fff;">
            Save scenario
          </A>
        )}
        <A onClick={() => p.go('track')} style="display:inline-flex;align-items:center;height:32px;padding:0 13px;border-radius:8px;font-size:16px;font-weight:600;text-decoration:none;white-space:nowrap;color:#2F5C97;border:1px solid rgba(47,92,151,.35);">
          Send to Tracking
        </A>
        <A onClick={memo} style="display:inline-flex;align-items:center;height:32px;padding:0 13px;border-radius:8px;font-size:16px;font-weight:600;text-decoration:none;white-space:nowrap;color:#2F5C97;border:1px solid rgba(47,92,151,.35);">
          Export IC memo
        </A>
        {anyChanged && (
          <A onClick={() => set({ dsIn: {} })} style="font-size:16px;color:#54606C;">
            Reset assumptions
          </A>
        )}
        <span style={sx('flex:1;')} />
        {saved.list.length === 0 && <span style={sx('font-size:14px;color:#54606C;')}>{saved.note ?? 'Saved scenarios appear here to compare.'}</span>}
        {saved.list.map((x) => (
          <span key={x.id} style={sx('display:inline-flex;align-items:center;gap:6px;height:28px;padding:0 9px;border-radius:7px;background:rgba(34,39,46,.06);font-size:14px;white-space:nowrap;')}>
            <b>{x.name}</b>
            <span style={sx('font-variant-numeric:tabular-nums;')}>
              <span>{x.irr}</span> &middot; <span>{x.moic}</span> &middot; cov <span>{x.cov}</span>
            </span>
          </span>
        ))}
      </div>
    </div>
  );
}

function FigRow({ r }: { r: [string, string, string, (string | null)?] }) {
  return (
    <div style={sx('display:grid;grid-template-columns:minmax(0,1fr) auto;gap:10px;align-items:baseline;padding:5px 0;border-bottom:1px solid rgba(34,39,46,.07);')}>
      <span style={sx('min-width:0;')}>
        <span style={sx('font-size:16px;font-weight:600;')}>{r[0]}</span> <span style={sx('font-size:14px;color:#54606C;')}>{r[2]}</span>
      </span>
      <span style={sx(PLAIN + (r[3] ? 'color:' + r[3] + ';' : ''))}>{r[1]}</span>
    </div>
  );
}

function InRow({ i, onChange }: { i: { k: string; label: string; unit: string; step: string; value: string }; onChange: (e: ChangeEvent<HTMLInputElement>) => void }) {
  return (
    <label style={sx('display:grid;grid-template-columns:minmax(0,1fr) 74px;gap:8px;align-items:center;padding:4px 0;border-bottom:1px solid rgba(34,39,46,.07);')}>
      <span style={sx('min-width:0;font-size:16px;color:#3C454F;overflow-wrap:break-word;')}>
        <span>{i.label}</span> <span style={sx('color:#54606C;font-size:14px;')}>{i.unit}</span>
      </span>
      <input type="number" step={i.step} value={i.value} onChange={onChange} aria-label={i.label} style={sx(INPUT) as CSSProperties} />
    </label>
  );
}

function Fig({ k, v }: { k: string; v: string }) {
  return (
    <div>
      <div style={sx('font-size:14px;color:#54606C;')}>{k}</div>
      <div style={sx('font-size:16px;font-weight:800;font-variant-numeric:tabular-nums;')}>{v}</div>
    </div>
  );
}
